import { useMemo } from 'react';
import {
  CustomizationDataDto,
  TableConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { TableCustomizationModal } from '@asset-metrix/portal-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  deleteTableCustomization,
  mutateTableCustomization,
} from '../../api/portalGateway/tableCustomization';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

import { mergeCustomizationWithConfig } from './mergeCustomizationWithConfig';

export type TableCustomizationProps = {
  customizationData: CustomizationDataDto | null;
  tableConfig: TableConfigDto;
  modalOpened: boolean;
  onClose: () => void;
  widgetId: string;
  isCustomized: boolean;
  withinPortal?: boolean;
};

export function TableCustomization({
  customizationData,
  tableConfig,
  modalOpened,
  onClose,
  widgetId,
  isCustomized,
  withinPortal = true,
}: TableCustomizationProps) {
  const mergedCustomization = useMemo(
    () => mergeCustomizationWithConfig(tableConfig, customizationData),
    [tableConfig, customizationData]
  );

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: mutateTableCustomization,
    onMutate: ({ widgetId, data }) => {
      queryClient.setQueryData(['customization', { widgetId }], { data });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTableCustomization,
    onMutate: ({ widgetId }) => {
      queryClient.setQueryData(['customization', { widgetId }], {
        data: {
          columns: [],
        },
      });
    },
  });

  return (
    <TableCustomizationModal
      initialValue={mergedCustomization}
      modalOpened={modalOpened}
      onCommit={(newValue) => {
        updateMutation.mutate({
          widgetId,
          data: {
            columns: [
              ...newValue.fixedColumns.map((c) => ({
                columnId: c.id,
                visible: c.isVisible,
                fixed: true,
              })),
              ...newValue.regularColumns.map((c) => ({
                columnId: c.id,
                visible: c.isVisible,
                fixed: false,
              })),
            ],
          },
        });
        onClose();
      }}
      onCancel={onClose}
      onReset={() => {
        deleteMutation.mutate({ widgetId });
        onClose();
      }}
      size={pixelSizeHelperPortalUi(650)}
      withinPortal={withinPortal}
      isCustomized={isCustomized}
    />
  );
}
